// THE SIGN-OFF TIMELINE (owner 2026-10-29, docs SIGNOFF-SPEC S1): the ring timeline on
// the left, the model in equal tiles in the middle, the selected tile on the right. The
// tiles are coloured for one date - the ring the timeline is scrolled to, a ring clicked,
// or the scrubber below the model, whose Play steps one day per second.
//
// MODEL (owner 2026-10-29): by default the real model streams in under the tiles, which
// are transparent (TileModelView); "Tiles" keeps the light tile-only view. The 3D volumes
// tagged on Levels, Areas & Systems are sign-off targets too, and can colour the tiles
// in their own colours ("Colour by: Volumes").
//
// Used by its own page (Coordination > Sign-off timeline) and, `embedded`, as the
// dashboard's tab after At a Glance.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Checkbox, Segmented, Select, Skeleton, Slider } from "antd";
import { CaretRightOutlined, PauseOutlined, StepBackwardOutlined, StepForwardOutlined } from "@ant-design/icons";
import moment from "moment";
import { Link } from "react-router-dom";
import { downloadExportChanges, downloadSignoffPrint, downloadSignoffSnapshot, getExportChanges, getGridLines, getModelPoints, getSignoffTiles, getSignoffTimeline, getTilesElements, getVolumeElements, signRevision } from "api/signoff";
import TileModelModal from "./TileModelModal";
import TileModelView from "./TileModelView";
import addNotification from "helpers/notify";
import { COLORS, GREY, BLUE, GOLD, RING, stateAt, tileColor, tilesOf, legendCounts, volumesByTile, innermost } from "./signoffState";
import RingTimeline, { ringColor } from "./RingTimeline";
import SignoffCertificate from "./SignoffCertificate";
import TileScene from "./TileScene";
import TilePanel from "./TilePanel";
import TilePicker from "./TilePicker";
import { gridText } from "./gridSpan";
import RequestSignoffModal from "./RequestSignoffModal";
import OpenRequests from "./OpenRequests";
import "./signoff.scss";

const DAY = 24 * 3600 * 1000;
const SPEEDS = [{ value: 1, label: "1 day / sec" }, { value: 7, label: "1 week / sec" }];
const GLASS = "#94a3b8";
const PICK = "#e8590c"; // tiles picked for a sign-off


export default function SignoffTimeline({ projectId, embedded = false, onLoaded, requestRef, captureRef }) {
  const [tiles, setTiles] = useState(null);
  const [note, setNote] = useState("");
  const [tl, setTl] = useState(null);
  const [error, setError] = useState("");
  const [at, setAt] = useState(() => new Date());
  const [activeId, setActiveId] = useState("");
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("3d");
  const [level, setLevel] = useState("");
  const [area, setArea] = useState("");
  const [outline, setOutline] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [asking, setAsking] = useState(null);
  const [showModel, setShowModel] = useState(false);
  const [points, setPoints] = useState(null);
  const [changed, setChanged] = useState({}); // revision ring id -> { tiles: Set of tile keys, uids: changed elements }
  const [viewing, setViewing] = useState(null);
  const [mode, setMode] = useState("model");
  const [colorBy, setColorBy] = useState("signoff");
  const [volume, setVolume] = useState("");
  const [volUids, setVolUids] = useState(null);
  const [frameSeq, setFrameSeq] = useState(0);

  const load = useCallback(async () => {
    if (!projectId) return;
    setError("");
    try {
      const [t, line] = await Promise.all([getSignoffTiles(projectId), getSignoffTimeline(projectId)]);
      setTiles(t.tiles);
      setNote(t.note || "");
      setTl(line);
      if (onLoaded) onLoaded({ tiles: t.tiles, timeline: line });
    } catch (e) {
      setError(e.message);
    }
  }, [projectId, onLoaded]);
  useEffect(() => { setTiles(null); setTl(null); setSelected(null); load(); }, [load]);
  // the building grid for the tile view (the model view fetches its own)
  const [gridLines, setGridLines] = useState(null);
  useEffect(() => {
    let live = true;
    if (projectId) getGridLines(projectId).then((r) => { if (live) setGridLines(r.lines || []); }).catch(() => {});
    return () => { live = false; };
  }, [projectId]);

  const list = useMemo(() => (tiles && tiles.tiles) || [], [tiles]);
  // every volume, removed ones too: a sign-off given on a volume before it was removed still
  // colours its tiles on that date. What is offered to pick or colour by is the live ones.
  const allVolumes = useMemo(() => (tl && tl.volumes) || [], [tl]);
  // the project's 3D volumes for the pickers - not the one-off tile selections made for a request
  const volumes = useMemo(() => allVolumes.filter((v) => !v.deleted && !v.adhoc), [allVolumes]);
  const volsOf = useMemo(() => volumesByTile(allVolumes), [allVolumes]);
  const liveVolsOf = useMemo(() => volumesByTile(volumes), [volumes]);
  const volById = useMemo(() => new Map(allVolumes.map((v) => [v.id, v])), [allVolumes]);
  const rings = useMemo(() => {
    const rs = (tl && tl.rings) || [];
    // how many tiles each sign-off covers
    return rs.map((r) => (r.type === "signoff" && r.target ? { ...r, tileCount: tilesOf(list, r.target, volsOf).length } : r));
  }, [tl, list, volsOf]);
  const states = useMemo(() => stateAt(rings, at), [rings, at]);
  const colorOf = useCallback((t) => COLORS[tileColor(t, states, volsOf.get(t.key) || []).level], [states, volsOf]);
  // the certificate open on screen, and the tiles it covers
  const [cert, setCert] = useState(null);
  // the request window is rendering its views: this page's model view steps aside
  const [capturing, setCapturing] = useState(false);
  // SELECTING TILES FOR A SIGN-OFF (TilePicker): the tiles picked, what a click does, the systems
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  // after picking / picked: its dependency list is read on every render, and reading them above
  // their declaration threw "Cannot access before initialization" and blanked the page (staging 2026-11-02)
  const sceneColorOf = useCallback((t) => (picking ? (picked.has(t.key) ? PICK : "#e5e7eb") : colorOf(t)), [picking, picked, colorOf]);
  const [pickMode, setPickMode] = useState("add");
  const [pickSystems, setPickSystems] = useState([]);
  const [madeVolumes, setMadeVolumes] = useState([]); // selections made here, for the request window's labels
  // what the selection takes in (owner 2026-11-01): every element inside or crossing the picked
  // tiles, of the ticked systems if any - tinted in the model, and nothing else is
  const [pickEls, setPickEls] = useState(null);
  useEffect(() => {
    if (!picking || !picked.size) { setPickEls(null); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      getTilesElements(projectId, [...picked], pickSystems).then((r) => { if (live) setPickEls(r); }).catch(() => { if (live) setPickEls(null); });
    }, 500);
    return () => { live = false; clearTimeout(t); };
  }, [picking, picked, pickSystems, projectId]);
  const onTileClick = useCallback((t) => {
    if (!picking) { setSelected(t); return; }
    if (!t) return;
    setPicked((p) => { const n = new Set(p); if (pickMode === "add") n.add(t.key); else n.delete(t.key); return n; });
  }, [picking, pickMode]);
  const certKeys = useMemo(() => (cert && cert.target ? tilesOf(list, cert.target, volsOf).map((t) => t.key) : []), [cert, list, volsOf]);
  // over the model: signed-off tiles (or a volume's, by volume) in colour, the rest as glass;
  // while selecting tiles, the picked ones in orange
  const styleOf = useCallback((t) => {
    if (picking) return picked.has(t.key) ? { color: PICK, strong: true } : { color: GLASS, strong: false };
    if (colorBy === "volumes") {
      const v = innermost(liveVolsOf.get(t.key), volById);
      return v ? { color: v.color, strong: true } : { color: GLASS, strong: false };
    }
    const l = tileColor(t, states, volsOf.get(t.key) || []).level;
    return l > GREY ? { color: COLORS[l], strong: true } : { color: GLASS, strong: false };
  }, [colorBy, states, volsOf, liveVolsOf, volById, picking, picked]);
  const visible = useCallback((t) => {
    if (!(t.g || []).length && !t.n) return false;
    if (level && !(t.g || []).some((g) => g.l === level)) return false;
    if (area && !(t.g || []).some((g) => g.a === area || (g.aa || []).includes(area))) return false;
    if (volume && !(volsOf.get(t.key) || []).includes(volume)) return false;
    return true;
  }, [level, area, volume, volsOf]);
  const active = rings.find((r) => r.id === activeId) || null;
  // the ring's tiles: a sign-off's targets, or the tiles an export changed
  const highlight = useMemo(() => {
    if (!outline || !active) return null;
    if (active.type === "export") return (changed[active.id] && changed[active.id].tiles) || null;
    return new Set(tilesOf(list, active.target, volsOf).map((t) => t.key));
  }, [outline, active, list, changed, volsOf]);
  // THE GRID LINES ROUND WHAT IS IN FOCUS, always on screen (owner 2026-11-02): the tiles being
  // selected, else the ring's, else the clicked tile, else the whole model's
  const gridKeys = useMemo(() => {
    if (picking && picked.size) return picked;
    if (active && active.type !== "export" && active.target) return tilesOf(list, active.target, volsOf).map((t) => t.key);
    if (selected) return [selected.key];
    return list.map((t) => t.key);
  }, [picking, picked, active, selected, list, volsOf]);
  const gridLabel = tiles ? gridText(gridLines, tiles.grid, gridKeys) : "";
  const [printing, setPrinting] = useState("");
  const print = (day) => {
    setPrinting(day || "all");
    downloadSignoffPrint(projectId, day).catch((e) => addNotification(e.message, "danger")).finally(() => setPrinting(""));
  };
  // a volume picked (or a volume ring clicked): its elements tinted in the model
  const focusVolume = volume || (active && active.target && active.target.kind === "volume" ? active.target.volumeId : "");
  const focusSystem = !volume && active && active.target && active.target.kind === "volume" ? active.target.system || "" : "";
  useEffect(() => {
    if (mode !== "model" || !focusVolume) { setVolUids(null); return undefined; }
    let live = true;
    getVolumeElements(projectId, focusVolume, focusSystem).then((r) => { if (live) setVolUids(r.uids); }).catch(() => { if (live) setVolUids(null); });
    return () => { live = false; };
  }, [mode, focusVolume, focusSystem, projectId]);
  useEffect(() => { if (volume) setFrameSeq((s) => s + 1); }, [volume]);
  // a revision: what it changed against the one before (the commit diff) - its tiles
  // outlined and, over the model, its added and changed elements highlighted
  useEffect(() => {
    if (!active || active.type !== "export" || changed[active.id]) return;
    const id = active.id;
    getExportChanges(projectId, id.slice(3))
      .then((c) => setChanged((m) => ({ ...m, [id]: { tiles: new Set((c.tiles || []).map((t) => t.key)), uids: c.uids || [] } })))
      .catch(() => setChanged((m) => ({ ...m, [id]: { tiles: new Set(), uids: [] } })));
  }, [active, changed, projectId]);
  const diffUids = active && active.type === "export" && changed[active.id] ? changed[active.id].uids : null;
  // the model inside the tiles, read once when first asked for
  useEffect(() => {
    if (!showModel || points) return;
    getModelPoints(projectId).then(setPoints).catch((e) => { addNotification(e.message, "danger"); setShowModel(false); });
  }, [showModel, points, projectId]);
  const counts = useMemo(() => {
    const shown = list.filter(visible);
    return legendCounts(shown, states, volsOf);
  }, [list, visible, states, volsOf]);

  // the scrubber's span: the first event to now
  const span = useMemo(() => {
    const times = rings.map((r) => new Date(r.at).getTime());
    const now = Date.now();
    const first = times.length ? Math.min(...times) : now - 30 * DAY;
    return { from: Math.min(first, now - DAY), to: now };
  }, [rings]);

  // A RING CLICKED REPLAYS UP TO IT (owner 2026-11-01: "the model view fades/transitions,
  // cumulative, from the first sign-off to the selected timeline sign-off"): the date steps
  // through every sign-off from the first to the picked one, about 2.5 s at most, the tiles
  // fading into each colour on the way (tileOverlay). Scrolling the rings stays instant.
  const replay = useRef(null);
  const stopReplay = () => { if (replay.current) { clearTimeout(replay.current); replay.current = null; } };
  useEffect(() => stopReplay, []);
  const pick = useCallback((r) => {
    stopReplay();
    setActiveId(r.id);
    setPlaying(false);
    const end = new Date(r.at).getTime();
    const stops = [...new Set(rings.filter((x) => x.type === "signoff").map((x) => new Date(x.at).getTime()).filter((t) => t <= end))].sort((a, b) => a - b);
    if (stops.length < 2) { setAt(new Date(r.at)); return; }
    if (stops[stops.length - 1] !== end) stops.push(end);
    const each = Math.max(120, Math.min(450, 2500 / stops.length));
    let i = 0;
    const next = () => {
      setAt(new Date(stops[i]));
      i += 1;
      replay.current = i < stops.length ? setTimeout(next, each) : null;
    };
    next();
  }, [rings]);
  const spy = useCallback((r) => { stopReplay(); setActiveId(r.id); setAt(new Date(r.at)); }, []);

  // Play: one step a second
  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => {
      setAt((d) => {
        const next = new Date(Math.min(d.getTime() + speed * DAY, span.to));
        if (next.getTime() >= span.to) setPlaying(false);
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [playing, speed, span.to]);
  // the ring the scrubber is on
  useEffect(() => {
    if (!playing) return;
    const r = rings.find((x) => new Date(x.at) <= at);
    if (r && r.id !== activeId) setActiveId(r.id);
  }, [at, playing, rings, activeId]);

  const step = (dir) => {
    const sorted = [...rings].sort((a, b) => new Date(a.at) - new Date(b.at));
    const t = at.getTime();
    const r = dir < 0 ? [...sorted].reverse().find((x) => new Date(x.at).getTime() < t - 1000) : sorted.find((x) => new Date(x.at).getTime() > t + 1000);
    if (r) pick(r);
  };

  const levelOptions = useMemo(() => {
    const ks = new Set();
    list.forEach((t) => (t.g || []).forEach((g) => g.l && ks.add(g.l)));
    return [...ks].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).map((k) => ({ value: k, label: (tl && tl.levels[k]) || k }));
  }, [list, tl]);
  const volumeOptions = useMemo(() => volumes.map((v) => {
    const p = v.parentId ? volById.get(v.parentId) : null;
    return { value: v.id, label: p ? `${p.name} › ${v.name}` : v.name };
  }), [volumes, volById]);
  const areaOptions = useMemo(() => Object.entries((tl && tl.areas) || {}).filter(([, a]) => a.current)
    .map(([id, a]) => ({ value: id, label: [a.name, a.level].filter(Boolean).join(" · ") }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })), [tl]);

  const openRequest = useCallback((tile) => {
    const pre = [];
    if (tile) {
      // the tile's innermost volume when it is in one, else its drawn areas
      const v = innermost(volsOf.get(tile.key), volById);
      if (v) pre.push({ kind: "volume", volumeId: v.id });
      else {
        const seen = new Set();
        (tile.g || []).forEach((g) => { if (g.a && !seen.has(g.a)) { seen.add(g.a); pre.push({ kind: "area", areaSetId: g.a }); } });
      }
    } else if (volume) pre.push({ kind: "volume", volumeId: volume });
    else if (area) pre.push({ kind: "area", areaSetId: area });
    else if (level) pre.push({ kind: "level", levelKey: level, level: (tl && tl.levels[level]) || level });
    setAsking({ targets: pre });
  }, [area, level, volume, tl, volsOf, volById]);
  useEffect(() => { if (requestRef) requestRef.current = () => openRequest(null); }, [requestRef, openRequest]);

  // a model revision's two stars, given here or asked for by link
  const rev = tl ? {
    canBlue: !!tl.canRequest, canGold: !!tl.canGold, canRequest: !!tl.canRequest,
    // a star is withdrawn by whoever gave it, or an admin / the project owner
    mayWithdraw: (s) => !!tl.canWithdrawAny || (!!s && !!s.byUserId && s.byUserId === tl.me),
    onStar: (r, star, withdraw) => signRevision(projectId, r.versionId, star, withdraw)
      .then(() => { addNotification(withdraw ? "Sign-off withdrawn" : `Revision #${r.number} signed off`, "success"); load(); })
      .catch((e) => addNotification(e.message, "danger")),
    onAsk: (r, star) => setAsking({ targets: [{ kind: "revision", versionId: r.versionId, label: `Model revision #${r.number}${r.exportVersion ? ` (${r.exportVersion})` : ""}` }], grant: star }),
  } : null;

  if (error) return <Alert type="error" showIcon message="The sign-off timeline could not be loaded" description={error} action={<Button onClick={load}>Retry</Button>} />;
  if (!tl || (!tiles && !note)) return <Skeleton active paragraph={{ rows: 10 }} />;

  const sceneH = embedded ? 480 : 560;
  return (
    <div className={`signoff-timeline ${embedded ? "is-embedded" : ""}`}>
      {embedded ? (
        <div className="signoff-embedded-bar">
          <span className="signoff-muted">The model in equal tiles, coloured by owner/GC and contractor sign-off on any date.</span>
          <span className="models-spacer" />
          {tiles ? <span className="signoff-grid-chip">Tiles: {tiles.grid.edgeM} m cubes · {tiles.grid.nx} × {tiles.grid.nz} × {tiles.grid.ny} grid · {list.length.toLocaleString()} with elements</span> : null}
          <Link to={`/signoff-timeline/${projectId}`}><Button>Open full page</Button></Link>
          {tl.canRequest ? <Button type="primary" onClick={() => openRequest(null)}>Request sign-off</Button> : null}
        </div>
      ) : null}
      <div className="signoff-grid">
        <RingTimeline rings={rings} activeId={activeId} onPick={pick} onSpy={spy}
                      onPdf={(r) => downloadSignoffSnapshot(projectId, r.id).catch((e) => addNotification(e.message, "danger"))}
                      onChanges={(r) => downloadExportChanges(projectId, r.id.slice(3)).catch((e) => addNotification(e.message, "danger"))} filter={filter} onFilter={setFilter}
                      search={search} onSearch={setSearch} height={sceneH + 40} rev={rev} onCertificate={setCert} />
        <div className="signoff-center">
          <div className="signoff-scene-bar">
            <Segmented value={mode} onChange={setMode} options={[{ value: "model", label: "Model" }, { value: "tiles", label: "Tiles" }]} data-testid="signoff-mode" />
            <Segmented value={view} onChange={setView} options={[{ value: "3d", label: "3D" }, { value: "plan", label: "Plan" }, { value: "section", label: "Section" }]} />
            <Select value={level} onChange={setLevel} style={{ minWidth: 120 }} options={[{ value: "", label: "All levels" }, ...levelOptions]} />
            <Select value={area} onChange={setArea} style={{ minWidth: 140 }} showSearch optionFilterProp="label" options={[{ value: "", label: "All areas" }, ...areaOptions]} />
            {volumes.length ? <Select value={volume} onChange={setVolume} style={{ minWidth: 150 }} showSearch optionFilterProp="label" options={[{ value: "", label: "All 3D volumes" }, ...volumeOptions]} /> : null}
            {volumes.length ? <Segmented value={colorBy} onChange={setColorBy} options={[{ value: "signoff", label: "Colour: sign-off" }, { value: "volumes", label: "Colour: volumes" }]} /> : null}
            <Checkbox checked={outline} onChange={(e) => setOutline(e.target.checked)}>Highlight the ring's tiles</Checkbox>
            {tl.canRequest && tiles ? (
              <Button type={picking ? "primary" : "default"} onClick={() => setPicking(!picking)} data-testid="select-tiles">{picking ? `Selecting tiles (${picked.size})` : "Select tiles"}</Button>
            ) : null}
            <Button onClick={() => print("")} loading={printing === "all"} data-testid="print-history">Print sign-off history</Button>
            <Button onClick={() => print(moment(at).format("YYYY-MM-DD"))} loading={!!printing && printing !== "all"} data-testid="print-day">Print {moment(at).format("MMM D, YYYY")}</Button>
            {mode === "tiles" ? <Checkbox checked={showModel} onChange={(e) => setShowModel(e.target.checked)}>Show model inside tiles</Checkbox> : null}
          </div>
          <div className="signoff-scene-date">
            <b>{moment(at).format("ddd, MMM D, YYYY · h:mm A")}</b>
            {active ? <span className="signoff-muted"> · {active.type === "export" ? `model export #${active.number}` : active.target.label}</span> : null}
            {gridLabel ? <span className="signoff-grid-chip ml-2" data-testid="timeline-grid">{gridLabel}</span> : null}
          </div>
          {tiles && mode === "model" && !capturing ? (
            <TileModelView projectId={projectId} tiles={tiles} styleOf={styleOf} visible={visible} outline={picking ? picked : highlight}
                           selectedKey={picking ? "" : selected ? selected.key : ""}
                           onPickTile={onTileClick} highlightUids={picking ? (pickEls ? pickEls.uids : []) : diffUids && diffUids.length ? diffUids : volUids} view={view} frameKeys={volume ? (volById.get(volume) || {}).tiles : null} frameSeq={frameSeq}
                           height={`${sceneH}px`} captureRef={captureRef} />
          ) : tiles ? (
            <TileScene tiles={tiles} colorOf={sceneColorOf} visible={visible} highlight={picking ? null : highlight} selectedKey={selected && selected.key} gridLines={gridLines}
                       view={view} onSelect={onTileClick} height={sceneH} captureRef={captureRef} points={showModel ? points : null} />
          ) : (
            <Alert className="m-3" type="info" showIcon message="No tiles yet" description={note} />
          )}
          {tiles ? (
            <div className="signoff-legend">
              <div><i style={{ background: COLORS[GREY] }} /> Not signed off <b>{counts[GREY].toLocaleString()}</b></div>
              <div><i style={{ background: COLORS[BLUE] }} /> Owner/GC signed off <span style={{ color: RING.ownerGc }}>★</span> <b>{counts[BLUE].toLocaleString()}</b></div>
              <div><i style={{ background: COLORS[GOLD] }} /> Contractor signed off <span style={{ color: RING.tenant }}>★</span> <b>{counts[GOLD].toLocaleString()}</b></div>
              <small className="signoff-muted">Empty tiles hidden · a partial tile shows its lowest level{mode === "model" ? " · tiles not yet signed off are clear glass" : ""}</small>
              {colorBy === "volumes" ? (
                <div className="signoff-volume-keys">{volumes.filter((v) => v.tiles.length).slice(0, 12).map((v) => <span key={v.id}><i style={{ background: v.color }} /> {v.name}</span>)}</div>
              ) : null}
              {tiles.elements && tiles.placed < tiles.elements ? <small className="signoff-muted d-block">{Math.round((tiles.placed / tiles.elements) * 100)}% of elements are in a drawn area; the rest stay grey</small> : null}
            </div>
          ) : null}
          <div className="signoff-scrubber">
            <Button icon={playing ? <PauseOutlined /> : <CaretRightOutlined />} onClick={() => { if (!playing && at.getTime() >= span.to - 60000) setAt(new Date(span.from)); setPlaying(!playing); }}>{playing ? "Pause" : "Play"}</Button>
            <Button icon={<StepBackwardOutlined />} onClick={() => step(-1)} aria-label="Previous event" />
            <Button icon={<StepForwardOutlined />} onClick={() => step(1)} aria-label="Next event" />
            <div className="signoff-track">
              <div className="signoff-ticks">
                {rings.map((r) => (
                  <i key={r.id} title={moment(r.at).format("MMM D")} style={{ left: `${((new Date(r.at).getTime() - span.from) / (span.to - span.from)) * 100}%`, background: ringColor(r) }} />
                ))}
              </div>
              <Slider min={span.from} max={span.to} step={3600 * 1000} value={Math.min(Math.max(at.getTime(), span.from), span.to)}
                      tooltip={{ formatter: (v) => moment(v).format("MMM D, YYYY") }}
                      onChange={(v) => { setPlaying(false); setAt(new Date(v)); }} />
              <div className="signoff-track-ends"><span>{moment(span.from).format("MMM D")}</span><span>Today</span></div>
            </div>
            <Select value={speed} onChange={setSpeed} options={SPEEDS} style={{ width: 120 }} />
          </div>
        </div>
        {picking ? (
          <TilePicker projectId={projectId} list={list} picked={picked} setPicked={setPicked} mode={pickMode} setMode={setPickMode}
                      systems={pickSystems} setSystems={setPickSystems} touching={pickEls} visible={visible} areas={tl.areas} levels={tl.levels} volumes={volumes} volsOf={volsOf}
                      onClose={() => setPicking(false)}
                      onRequest={(vol, targets) => {
                        setMadeVolumes((m) => [...m, vol]);
                        setPicking(false); setPicked(new Set()); setPickSystems([]);
                        setAsking({ targets });
                      }} />
        ) : (
          <TilePanel tile={selected} tiles={tiles} states={states} areas={tl.areas} levels={tl.levels} rings={rings} at={at}
                     volumes={volById} vols={selected ? liveVolsOf.get(selected.key) || [] : []}
                     canRequest={tl.canRequest} onRequest={openRequest} onOpen3d={tiles ? setViewing : null} />
        )}
      </div>
      {viewing ? <TileModelModal projectId={projectId} tile={viewing} onClose={() => setViewing(null)} /> : null}
      {cert ? (
        <SignoffCertificate projectId={projectId} projectTitle={tl.projectTitle} ring={cert} tiles={tiles} coveredKeys={certKeys} colorOf={colorOf} gridLines={gridLines}
                            onPdf={(r) => downloadSignoffSnapshot(projectId, r.id).catch((e) => addNotification(e.message, "danger"))} onClose={() => setCert(null)} />
      ) : null}
      {asking ? (
        <RequestSignoffModal projectId={projectId} areas={tl.areas} levels={tl.levels} tiles={list} tileView={tiles} volumes={[...volumes, ...madeVolumes]} initial={asking.targets} defaultGrant={asking.grant || "ownerGc"}
                             onClose={() => setAsking(null)} onSent={() => { setAsking(null); load(); }} onCapturing={setCapturing} />
      ) : null}
      <OpenRequests projectId={projectId} requests={tl.requests} canRequest={tl.canRequest} onChanged={load} />
    </div>
  );
}

